/**
 * Cross-section action items grid.
 *
 * Compact one-line rows. Title truncates with ellipsis; full title in the
 * native HTML title attribute. Whole row (except the Notes cell) is the click
 * target — opens the report at the originating A# anchor.
 *
 * #118: free-text search, filter by report + by type (A-code), and click-header
 * sorting — composed via useCollection over the (status-filtered) list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  Header,
  SpaceBetween,
  Button,
  StatusIndicator,
  Select,
  Pagination,
  Spinner,
  Badge,
  TextFilter,
  type SelectProps,
} from "@cloudscape-design/components";
import { useCollection } from "@cloudscape-design/collection-hooks";
import type { ActionItem } from "@/core/api/types";
import { listActionItems } from "@/core/api/action-items";
import { CommentsCell } from "@/core/ui/CommentsCell";
import { formatETDate } from "@/core/ui/time";

interface Props {
  onOpenActionItem: (reportId: string, actionItemId: string) => void;
  /** #132: take a first-time user to the Reports tab to upload their first SOC 1. */
  onNavigateReports?: () => void;
}

type StatusOption = { label: string; value: "open" | "closed" | "all" };
const STATUS_OPTIONS: StatusOption[] = [
  { label: "Open only", value: "open" },
  { label: "Closed only", value: "closed" },
  { label: "All", value: "all" },
];
const DEFAULT_STATUS: StatusOption = STATUS_OPTIONS[0]!;
const PAGE_SIZE = 50;
const ALL_REPORTS: SelectProps.Option = { label: "All reports", value: "all" };
const ALL_TYPES: SelectProps.Option = { label: "All types", value: "all" };

const SECTION_COLOR: Record<ActionItem["section"], "blue" | "green" | "red" | "grey"> = {
  scope: "blue",
  bridge: "blue",
  controls: "red",
  cuecs: "green",
  docs: "grey",
};

// Display label per section. Enum value `controls` renders as "exceptions"
// after #71 — the section was renamed to "Control Exceptions" but the DB
// enum stays `controls`.
const SECTION_LABEL: Record<ActionItem["section"], string> = {
  scope: "scope",
  bridge: "bridge",
  controls: "exceptions",
  cuecs: "cuecs",
  docs: "docs",
};

const oneLine: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: "pointer",
};

// #111: the Report cell jammed vendor + scope title together and overflowed.
// Truncate each to ~30 chars with "…" and show the full text on hover.
const TRUNC = 30;
const truncate = (s: string) => (s.length > TRUNC ? `${s.slice(0, TRUNC)}…` : s);
const lastModified = (it: ActionItem) => it.closed_at ?? it.opened_at;

export function ActionItemsInbox({ onOpenActionItem, onNavigateReports }: Props) {
  const [allItems, setAllItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusOption>(DEFAULT_STATUS);
  const [reportFilter, setReportFilter] = useState<SelectProps.Option>(ALL_REPORTS);
  const [typeFilter, setTypeFilter] = useState<SelectProps.Option>(ALL_TYPES);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = statusFilter.value === "all" ? undefined : statusFilter.value;
      setAllItems(await listActionItems({ status }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // #118: distinct report + type (A-code) options, derived from the loaded set.
  const reportOptions = useMemo<SelectProps.Option[]>(() => {
    const seen = new Map<string, string>();
    for (const it of allItems) if (!seen.has(it.report_id)) seen.set(it.report_id, it.report_vendor || "(untitled)");
    return [ALL_REPORTS, ...[...seen].map(([id, name]) => ({ label: name, value: id }))];
  }, [allItems]);
  const typeOptions = useMemo<SelectProps.Option[]>(() => {
    const codes = [...new Set(allItems.map((it) => it.a_code))].sort();
    return [ALL_TYPES, ...codes.map((c) => ({ label: c, value: c }))];
  }, [allItems]);

  // Apply the dropdown filters before the text/sort/paginate collection.
  const scoped = useMemo(
    () =>
      allItems.filter(
        (it) =>
          (reportFilter.value === "all" || it.report_id === reportFilter.value) &&
          (typeFilter.value === "all" || it.a_code === typeFilter.value)
      ),
    [allItems, reportFilter, typeFilter]
  );

  const { items, collectionProps, filterProps, filteredItemsCount, paginationProps } = useCollection(scoped, {
    filtering: {
      filteringFunction: (it, text) => {
        const q = (text ?? "").toLowerCase();
        if (!q) return true;
        return [it.code, it.a_code, it.title, it.report_vendor, it.report_scope_title]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      },
    },
    sorting: { defaultState: { sortingColumn: { sortingField: "opened_at" }, isDescending: true } },
    pagination: { pageSize: PAGE_SIZE },
  });

  if (loading && !allItems.length) {
    return (
      <Box padding="l" textAlign="center">
        <Spinner /> Loading action items…
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="l">
        <StatusIndicator type="error">{error}</StatusIndicator>
      </Box>
    );
  }

  // #C11: a carve-out "Request SOC 1" item deep-links to its placeholder report
  // (its upload view) rather than the host report.
  const targetReport = (it: ActionItem) => it.link_report_id ?? it.report_id;
  const openCell = (it: ActionItem, children: React.ReactNode) => (
    <div style={oneLine} title={it.title} onClick={() => onOpenActionItem(targetReport(it), it.id)}>
      {children}
    </div>
  );

  return (
    <Table
      {...collectionProps}
      items={items}
      header={
        <Header
          variant="h1"
          counter={`(${allItems.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Select
                selectedOption={statusFilter}
                onChange={({ detail }) =>
                  setStatusFilter(
                    STATUS_OPTIONS.find((o) => o.value === detail.selectedOption.value) ?? DEFAULT_STATUS
                  )
                }
                options={STATUS_OPTIONS}
              />
              <Button onClick={refresh} iconName="refresh" disabled={loading} />
            </SpaceBetween>
          }
        >
          Action items
        </Header>
      }
      filter={
        <SpaceBetween direction="horizontal" size="xs">
          <TextFilter
            {...filterProps}
            filteringPlaceholder="Find by code, type, title, or report…"
            countText={`${filteredItemsCount} match${filteredItemsCount === 1 ? "" : "es"}`}
          />
          <Select
            selectedOption={reportFilter}
            onChange={({ detail }) => setReportFilter(detail.selectedOption)}
            options={reportOptions}
            ariaLabel="Filter by report"
          />
          <Select
            selectedOption={typeFilter}
            onChange={({ detail }) => setTypeFilter(detail.selectedOption)}
            options={typeOptions}
            ariaLabel="Filter by type"
          />
        </SpaceBetween>
      }
      pagination={<Pagination {...paginationProps} />}
      columnDefinitions={[
        {
          id: "code",
          header: "Code",
          cell: (it) => openCell(it, <Box variant="strong">{it.code}</Box>),
          sortingField: "code",
          width: 100,
          minWidth: 90,
        },
        {
          id: "a_code",
          header: "Type",
          cell: (it) => openCell(it, <Badge color="blue">{it.a_code}</Badge>),
          sortingField: "a_code",
          width: 80,
          minWidth: 70,
        },
        {
          id: "section",
          header: "Section",
          cell: (it) =>
            openCell(it, <Badge color={SECTION_COLOR[it.section]}>{SECTION_LABEL[it.section]}</Badge>),
          sortingField: "section",
          width: 100,
          minWidth: 90,
        },
        {
          id: "report",
          header: "Report",
          sortingComparator: (a, b) => (a.report_vendor ?? "").localeCompare(b.report_vendor ?? ""),
          cell: (it) => {
            const vendor = it.report_vendor || "—";
            const scope = it.report_scope_title || "";
            return (
              <div style={{ cursor: "pointer" }} onClick={() => onOpenActionItem(targetReport(it), it.id)}>
                <div style={oneLine} title={vendor}>
                  <Box variant="strong">{truncate(vendor)}</Box>
                </div>
                {scope ? (
                  <div style={oneLine} title={scope}>
                    <Box variant="small" color="text-body-secondary">
                      {truncate(scope)}
                    </Box>
                  </div>
                ) : null}
              </div>
            );
          },
          width: 220,
          minWidth: 160,
        },
        {
          id: "title",
          header: "Title",
          cell: (it) => openCell(it, it.title),
          sortingField: "title",
          width: 320,
          minWidth: 180,
          maxWidth: 440,
        },
        {
          id: "comments",
          header: "Notes",
          cell: (it) => (
            <CommentsCell
              kind="action-item"
              targetId={it.id}
              targetLabel={it.code}
              count={it.comment_count ?? 0}
              onChange={() => void refresh()}
            />
          ),
          width: 90,
          minWidth: 70,
        },
        {
          id: "last_modified",
          header: "Last modified",
          cell: (it) => openCell(it, formatETDate(lastModified(it))),
          sortingComparator: (a, b) => lastModified(a).localeCompare(lastModified(b)),
          width: 130,
          minWidth: 110,
        },
      ]}
      empty={
        <Box textAlign="center" padding="l">
          <SpaceBetween size="s">
            <Header variant="h3">Nothing in your inbox</Header>
            <Box color="text-body-secondary">
              Start here: upload a SOC 1 in the Reports tab. The app extracts the facts and surfaces only the
              decisions you need — they'll appear here as action items.
            </Box>
            {onNavigateReports && (
              <Box>
                <Button variant="primary" iconName="upload" onClick={onNavigateReports}>
                  Go to Reports
                </Button>
              </Box>
            )}
          </SpaceBetween>
        </Box>
      }
    />
  );
}
